import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useRecoilState, useRecoilValue } from 'recoil';

import { alertsTestIDs, mapPageTestIDs, myWeatherPageTestIDs } from '@constants';
import { helpers } from '@helpers';
import { useContextNodeHandle } from '@hooks';
import { navigationService } from '@services';
import { alertsHeadlinesState, screenReaderEnabledState, userSettingsState } from '@store';

import { tPath } from './constants';
import { styles } from './styles';

import { TWCButton } from '../../focusable/TWCButton/TWCButton';
import { TWCTileContent } from '../../focusable/TWCTileContent/TWCTileContent';
import { TWCText } from '../../primitives/TWCText/TWCText';

export const AlertOverlay: FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });
  const [screenReaderEnabled] = useRecoilState(screenReaderEnabledState);
  const { locations } = useRecoilValue(userSettingsState);
  const defaultLocation = locations.find(location => location.isDefault);
  const { alertOverlays } = useRecoilValue(alertsHeadlinesState);
  const alert = alertOverlays && alertOverlays?.length > 0 ? alertOverlays[0] : null;
  const eventDescription = alert?.eventDescription;

  const viewRadarButtonHandle = useContextNodeHandle(alertsTestIDs.overlay.viewRadarButton);
  const dismissButtonHandle = useContextNodeHandle(alertsTestIDs.overlay.dismissButton);

  // Location based on alert data
  const location = `${defaultLocation?.city}, ${defaultLocation?.state}`;

  const dismissAlert = () => navigationService.goBack();

  useEffect(() => {
    if (helpers.isWeb) {
      setTimeout(() => {
        helpers.FOCUS_NOW(viewRadarButtonHandle);
      }, 10);
    }
  }, [viewRadarButtonHandle]);

  return (
    <TWCTileContent gradientType={'alertTile'} height={500} width={640}>
      <View accessibilityRole={'text'} accessible={screenReaderEnabled} style={styles.alertModalContent}>
        <TWCText
          importantForAccessibility={'no'}
          style={styles.subText}
          testID={alertsTestIDs.overlay.subText}
          variant={'regularText30'}
        >
          {`${t('headingText')} ${location}:`}
        </TWCText>
        <TWCText
          importantForAccessibility={'no'}
          style={styles.titleText}
          testID={alertsTestIDs.overlay.headlineText}
          variant={'mediumText36'}
        >
          {eventDescription}
        </TWCText>
        <TWCButton
          accessibilityHint={t('viewRadarButtonAccessibilityHint')}
          accessibilityLabel={`${t('headingText')} ${location}: ${eventDescription} ${t(
            'viewRadarButtonAccessibilityLabel',
          )}`}
          nextFocusDown={dismissButtonHandle}
          nextFocusLeft={null}
          nextFocusRight={null}
          nextFocusUp={null}
          testID={alertsTestIDs.overlay.viewRadarButton}
          title={t('viewRadarButtonText')}
          onPress={() => {
            dismissAlert();
            navigationService.navigate(myWeatherPageTestIDs.myWeatherPage);
            navigationService.navigate(mapPageTestIDs.mapPage, {
              useDefaultLocation: !defaultLocation?.isWatching,
            });
          }}
        />
        <TWCButton
          accessibilityHint={t('dismissButtonAccessibilityHint')}
          accessibilityLabel={t('dismissButtonAccessibilityLabel')}
          nextFocusDown={null}
          nextFocusLeft={null}
          nextFocusRight={null}
          nextFocusUp={viewRadarButtonHandle}
          style={styles.dismissButtonText}
          testID={alertsTestIDs.overlay.dismissButton}
          title={t('dismissButtonText')}
          onPress={dismissAlert}
        />
      </View>
    </TWCTileContent>
  );
};
